import { defineStore } from "pinia"
import { HttpService } from "@/helpers/http-service"
import { CONSTANT } from "@/helpers/constant"
import { cloneDeep, get, isEmpty, isEqual, isString, set, sortBy } from "lodash"
import { ApiResponse } from "@/types/apiResponse"
import {
  _getValue,
  checkConfigurableV3,
  checkOnlyPrefilledCustomizationV3,
  getCalculatedPriceObject,
  getCustomConfigurationArrayV3,
  getCustomisationName,
  getCustomizationNameFromOption,
  getMultiplicationOfTwoNumbersWithFractionDigits,
  getOptionPrice,
  getPreFilledInputCustomisation,
  getSumOfTwoNumbersWithFractionDigits,
  getTotalPlayerCount,
  getTotalValue,
  getTypeKey,
  getUserInputCustomisation,
  getValueLength,
  initRegExForExpertMode,
  isValidationErrorInExpertMode,
  parseConfigurationFromItemObject,
  prepareCartObject,
  removeEmptyOptionalCustomizationV2,
  sortChildren,
} from "@/helpers/common"
import { _calculateFinishesPriceInNumber, getAssortmentProductMediaCoverUrl, getAssortmentProductMediaUrl } from "@/helpers/assortment-item-service"
import { EecomTpAssortmentProduct } from "@/types/eecomTpAssortmentProduct"
import { useCustomerStore } from "./customer.store"
import { OfferPayload } from "@/types/offer"
import { FinalConfiguration, FinalConfigurationHeader, ProductQuantity } from "@/types/teamOrder"
import { Product } from "@/types/product"
import { IndexedDbService } from "@/helpers/indexed-db-service"
import { getOfferProductPosition, offerConfigDBStoreName, sortOfferProductsByPosition } from "@/helpers/offer"
import { LineItem } from "@/models/addToCart.model"
import { IndexDBChanged } from "@/helpers/behavior-service"
import uuid from "@/helpers/uuid"
import formatPrice from "@/helpers/format-price"

const httpService = new HttpService()
const httpServiceWithoutLoader = new HttpService(false)

interface State {
  offers: any,
  offer: any,
  customerOffers: any,
  customerOffer: any,
  offerAssortmentProducts: any,
  finalConfiguration: FinalConfiguration,
  dirtyProductIds: Set<string>,
  hasUnsavedChanges: boolean,
  customizationTypeObject: any,
  isAdidas: boolean
}
export const useOfferStore = defineStore({
  id: "offer-v2",
  state: (): State => ({
    offers: [],
    offer: {},
    customerOffers: [],
    customerOffer: {},
    offerAssortmentProducts: [],
    dirtyProductIds: new Set<string>(),
    hasUnsavedChanges: false,
    finalConfiguration: {} as FinalConfiguration,
    customizationTypeObject: {
      initials: {
        mandatoryCustomization: {},
        optionalCustomization: {},
      },
      playername: {
        mandatoryCustomization: {},
        optionalCustomization: {},
      },
      number: {
        mandatoryCustomization: {},
        optionalCustomization: {},
      },
      expertMode: {
        mandatoryCustomization: {},
        optionalCustomization: {},
      },
    },
    isAdidas: process.env.VUE_APP_ENV === "adidas"
  }),
  getters: {
    sortedOfferProducts(state): any[] {
      return sortOfferProductsByPosition(state.offer?.eecomTpAssortment?.eecomTpAssortmentProducts || [])
    },
    sortedCustomerOfferProducts(state): any[] {
      return sortOfferProductsByPosition(state.offerAssortmentProducts || [])
    },
  },
  actions: {
    async fetch(bodyParams: any = {}, customerId: any) {
      if (!customerId || !isString(customerId)) {
        return
      }

      try {
        const fetchSalesRepCustomerOfferUrl = CONSTANT.API.OFFER.FETCH_OFFERS.replace("{customerId}", customerId)
        const result: ApiResponse = await httpService.post(fetchSalesRepCustomerOfferUrl, bodyParams)
        if (result && result.status === CONSTANT.HTTP_STATUS.OK) {
          this.offers = get(result, "data", null)
          return true
        }
      } catch (error) {
        const errors = get(error, `response.data.errors`, [])
        if (errors && errors.length > 0) {
          // @ts-ignore
          console.error(errors[0].detail)
        }
        console.error(error)
      }
    },

    async fetchCustomerOffer(customerId: string, bodyParams: any = {}, isDisplayLoader: boolean = true) {
      this.customerOffers = []
      if (!customerId || !isString(customerId)) {
        return
      }

      try {
        const url = CONSTANT.API.OFFER.FETCH_CUSTOMER_OFFERS.replace("{customerId}", customerId)
        let result: ApiResponse = {} as ApiResponse;
        if (isDisplayLoader) {
          result = await httpService.post(url, bodyParams)
        } else {
          result = await httpServiceWithoutLoader.post(url, bodyParams)
        }
        if (result && result.status === CONSTANT.HTTP_STATUS.OK) {
          this.customerOffers = get(result, "data", [])
          return true
        }
      } catch (error) {
        const errors = get(error, `response.data.errors`, [])
        if (errors && errors.length > 0) {
          // @ts-ignore
          console.error(errors[0].detail)
        }
        console.error(error)
        this.customerOffers = []
        return false
      }
    },

    async fetchOfferById(
      customerId: string,
      offerId: string,
      bodyParams: any = {},
      isDisplayLoader: boolean = true,
      options: { preserveLocalEdits?: boolean } = {},
    ) {
      const preserveLocalEdits = options.preserveLocalEdits !== false
      const currentOfferId = this.offer?.id
      const isOfferSwitch = Boolean(currentOfferId && currentOfferId !== offerId)
      if (isOfferSwitch) {
        this.hasUnsavedChanges = false
        this.dirtyProductIds.clear()
      }
      const snapshot = preserveLocalEdits && !isOfferSwitch ? this.captureLocalOfferSnapshot() : null
      const previousHasUnsaved = this.hasUnsavedChanges
      const previousDirtyIds = new Set(this.dirtyProductIds)

      if (!customerId || !isString(customerId) || !offerId || !isString(offerId)) {
        return
      }
      let result: ApiResponse = {} as ApiResponse;
      const fetchOfferUrl = CONSTANT.API.OFFER.FETCH_OFFERS.replace("{customerId}", customerId)
      if (isDisplayLoader) {
        result = await httpService.post(fetchOfferUrl, bodyParams)
      } else {
        result = await httpServiceWithoutLoader.post(fetchOfferUrl, bodyParams)
      }
      if (result && result.status === CONSTANT.HTTP_STATUS.OK) {
        const fetched = result.data?.elements?.[0]
        if (!fetched) {
          return
        }
        if (snapshot && snapshot.offerId === offerId && (previousHasUnsaved || previousDirtyIds.size > 0)) {
          this.offer = this.mergeFetchedOfferWithLocalSnapshot(fetched, {
            ...snapshot,
            hasUnsavedChanges: previousHasUnsaved,
            dirtyProductIds: previousDirtyIds,
          })
          this.hasUnsavedChanges = previousHasUnsaved
          this.dirtyProductIds = previousDirtyIds
        } else if (snapshot?.offerId === offerId && snapshot?.name && snapshot.name !== fetched.name) {
          this.offer = { ...fetched, name: snapshot.name }
        } else {
          this.offer = fetched
          if (!preserveLocalEdits || isOfferSwitch) {
            this.hasUnsavedChanges = false
            this.dirtyProductIds.clear()
          }
        }
      }
    },

    captureLocalOfferSnapshot() {
      const offer = this.offer
      if (!offer?.id) {
        return null
      }
      return {
        offerId: offer.id,
        name: offer.name,
        paymentMethodId: offer.paymentMethodId,
        products: (offer.eecomTpAssortment?.eecomTpAssortmentProducts || []).map((product: any) =>
          cloneDeep({
            id: product.id,
            name: product.name,
            translated: product.translated,
            price: product.price,
            calculatedPrice: product.calculatedPrice,
            configuration: product.configuration,
            eecomTpOfferAssortmentProductConfigs: product.eecomTpOfferAssortmentProductConfigs,
            eecomTpMandatoryCustomizations: product.eecomTpMandatoryCustomizations,
            eecomTpOptionalCustomizations: product.eecomTpOptionalCustomizations,
          }),
        ),
      }
    },

    mergeFetchedOfferWithLocalSnapshot(fetched: any, snapshot: any) {
      if (!fetched) {
        return fetched
      }
      const merged = { ...fetched }
      const hasProductEdits =
        snapshot?.hasUnsavedChanges || (snapshot?.dirtyProductIds && snapshot.dirtyProductIds.size > 0)

      if (snapshot?.name && snapshot.name !== fetched.name) {
        merged.name = snapshot.name
      }
      if (snapshot?.paymentMethodId !== undefined && snapshot.paymentMethodId !== fetched.paymentMethodId) {
        merged.paymentMethodId = snapshot.paymentMethodId
      }

      if (!hasProductEdits) {
        return merged
      }

      const fetchedProducts = merged.eecomTpAssortment?.eecomTpAssortmentProducts
      if (!Array.isArray(fetchedProducts) || !snapshot?.products?.length) {
        return merged
      }

      const localProductsById = new Map(snapshot.products.map((product: any) => [product.id, product]))
      merged.eecomTpAssortment = {
        ...merged.eecomTpAssortment,
        eecomTpAssortmentProducts: fetchedProducts.map((fetchedProduct: any) => {
          const localProduct: any = localProductsById.get(fetchedProduct.id)
          if (!localProduct || !snapshot.dirtyProductIds?.has(fetchedProduct.id)) {
            return fetchedProduct
          }

          return {
            ...fetchedProduct,
            name: localProduct.name ?? fetchedProduct.name,
            translated: localProduct.translated ?? fetchedProduct.translated,
            price: localProduct.price ?? fetchedProduct.price,
            calculatedPrice: localProduct.calculatedPrice ?? fetchedProduct.calculatedPrice,
            configuration: localProduct.configuration ?? fetchedProduct.configuration,
            eecomTpOfferAssortmentProductConfigs:
              localProduct.eecomTpOfferAssortmentProductConfigs ?? fetchedProduct.eecomTpOfferAssortmentProductConfigs,
            eecomTpMandatoryCustomizations:
              localProduct.eecomTpMandatoryCustomizations ?? fetchedProduct.eecomTpMandatoryCustomizations,
            eecomTpOptionalCustomizations:
              localProduct.eecomTpOptionalCustomizations ?? fetchedProduct.eecomTpOptionalCustomizations,
          }
        }),
      }

      return merged
    },

    async fetchOfferAssortmentProducts(customerId: string, offerId: string, bodyParams: any = {}, isDisplayLoader: boolean = true) {
      try {
        const url = CONSTANT.API.OFFER.FETCH_OFFER_ASSORTMENT_PRODUCTS.replace("{customerId}", customerId).replace("{id}", offerId)
        let result: any
        if (isDisplayLoader) {
          result = await httpService.post(url, bodyParams)
        } else {
          result = await httpServiceWithoutLoader.post(url, bodyParams)
        }
        if (result && result.status === CONSTANT.HTTP_STATUS.OK) {
          const products = get(result, "data.elements", [])
          if (this.offer && this.offer.eecomTpAssortment) {
            this.offer.eecomTpAssortment.eecomTpAssortmentProducts = products
          } else if (this.offer) {
            this.offer.eecomTpAssortment = { eecomTpAssortmentProducts: products }
          }
          return products
        }
      } catch (error) {
        console.error("Failed to fetch offer assortment products:", error)
      }
      return []
    },

    async createOfferWithAssortment(customerId: string, offerPayload: OfferPayload) {
      const createOfferWithAssortmentUrl = CONSTANT.API.OFFER.CREATE_OFFER_WITH_ASSORTMENT.replace("{customerId}", customerId)
      const result: ApiResponse = await httpService.post(createOfferWithAssortmentUrl, offerPayload)
      if (result && result.status === CONSTANT.HTTP_STATUS.OK) {
        return result.data
      }
      return false
    },

    async update(customerId: string, offerId: string, bodyParams: any, isDisplayLoader: boolean = true) {
      try {
        let result: ApiResponse = {} as ApiResponse;
        const updateOfferStatusUrl = CONSTANT.API.OFFER.UPDATE_OFFERS.replace("{id}", offerId).replace("{customerId}", customerId)
        if (isDisplayLoader) {
          result = await httpService.patch(updateOfferStatusUrl, bodyParams)
        } else {
          result = await httpServiceWithoutLoader.patch(updateOfferStatusUrl, bodyParams)
        }
        if (result.status === CONSTANT.HTTP_STATUS.OK || result.status === CONSTANT.HTTP_STATUS.NO_CONTENT) {
          return true;
        } else {
          return false;
        }
      } catch (error) {
        const errorDetail = get(error, "response.data.errors[0].detail") || get(error, "message") || error
        console.error("offerStore.update failed:", errorDetail)
        return false;
      }
    },

    async delete(customerId: string, offerId: string) {
      try {
        const deleteOfferUrl = CONSTANT.API.OFFER.DELETE_OFFERS.replace("{id}", offerId).replace("{customerId}", customerId)
        const result: ApiResponse = await httpService.delete(deleteOfferUrl)
        if (result && result.status === CONSTANT.HTTP_STATUS.OK) {
          return true;
        } else {
          return false;
        }
      } catch (error) {
          return false;
      }
    },

    isConfigurationSaveSuccess(result: any): boolean {
      if (Array.isArray(result) && result.length > 0) {
        return false
      }
      const status = get(result, "status")
      if (
        status &&
        status !== CONSTANT.HTTP_STATUS.OK &&
        status !== CONSTANT.HTTP_STATUS.NO_CONTENT
      ) {
        return false
      }
      return true
    },

    async saveOfferProductPosition(product: any, position: number) {
      const configs = product?.eecomTpOfferAssortmentProductConfigs
      if (!configs?.length) {
        return true
      }

      const bodyParams = {
        id: product.id,
        eecomTpOfferAssortmentProductConfigs: configs.map((config: any) => ({
          id: config.id,
          eecomTpAssortmentProductId: product.id,
          productId: config.productId,
          quantity: Number(config.quantity),
          position: Number(position),
        })),
      }

      const result = await this.setConfiguration(bodyParams, false)
      return this.isConfigurationSaveSuccess(result)
    },

    async revertOfferProductPositions(
      snapshots: Array<{ product: any; originalPosition: number }>,
      indices: number[],
    ): Promise<boolean> {
      if (indices.length === 0) {
        return true
      }

      try {
        const results = await Promise.allSettled(
          indices.map(async (index) => {
            const { product, originalPosition } = snapshots[index]
            const revertPosition = originalPosition === Number.MAX_SAFE_INTEGER
              ? index + 1
              : originalPosition
            return this.saveOfferProductPosition(product, revertPosition)
          }),
        )
        return results.every((result) => result.status === "fulfilled" && result.value === true)
      } catch (error) {
        console.error(error)
        return false
      }
    },

    async updateOfferProducts(products: any[]): Promise<{
      success: boolean
      partial?: boolean
      revertFailed?: boolean
    }> {
      const productsToUpdate = products.filter((product) => product?.eecomTpOfferAssortmentProductConfigs?.length)
      const snapshots = productsToUpdate.map((product) => ({
        product,
        originalPosition: getOfferProductPosition(product),
      }))

      if (snapshots.length === 0) {
        return { success: true }
      }

      try {
        const saveOutcomes = await Promise.all(
          snapshots.map(async (snapshot, index) => ({
            index,
            saved: await this.saveOfferProductPosition(snapshot.product, snapshot.product.position),
          })),
        )

        const failedOutcomes = saveOutcomes.filter((outcome) => !outcome.saved)
        if (failedOutcomes.length === 0) {
          return { success: true }
        }

        const succeededIndices = saveOutcomes
          .filter((outcome) => outcome.saved)
          .map((outcome) => outcome.index)

        const revertOk = await this.revertOfferProductPositions(snapshots, succeededIndices)

        return {
          success: false,
          partial: succeededIndices.length > 0,
          revertFailed: !revertOk,
        }
      } catch (error) {
        const errors: any = get(error, `response.data.errors`, [])
        if (errors?.length > 0) {
          console.error(errors[0].detail)
        }
        console.error(error)
        return { success: false, partial: false, revertFailed: true }
      }
    },

    async removeOfferProductById(customerId: string, offerProductId: string) {
      try {
        const url = CONSTANT.API.OFFER.DELETE_OFFER_PRODUCT_BY_ID.replace("{id}", offerProductId).replace("{customerId}", customerId)
        const result: ApiResponse = await httpService.delete(url)
        if (result && result.status === CONSTANT.HTTP_STATUS.NO_CONTENT) {
          return true;
        } else {
          return false;
        }
      } catch (error) {
          return false;
      }
    },

    async fetchCustomerOfferProducts(customerId: string, offerId: string, bodyParams: any) {
      try {
        const url = CONSTANT.API.OFFER.FETCH_CUSTOMER_OFFERS_DETAILS.replace("{id}", offerId).replace("{customerId}", customerId)
        let result: any
        result = await httpService.post(url, bodyParams)

        if (result && result.status === CONSTANT.HTTP_STATUS.OK) {
          const products = get(result, "data.elements[0].eecomTpAssortment.eecomTpAssortmentProducts", [])
          this.offerAssortmentProducts = sortOfferProductsByPosition(products)
          this.customerOffer = get(result, "data", {})
        } else {
          this.offerAssortmentProducts = []
          this.customerOffer = {}
        }
      } catch (error) {
        const errors = get(error, `response.data.errors`, [])
        if (errors && errors.length > 0) {
          // @ts-ignore
          console.error(errors[0].detail)
        }
        console.error(error)
        this.offerAssortmentProducts = []
        this.customerOffer = {}
      }
    },

    async createPdf(customerId: string, bodyParams: any) {
      try {
        const url = CONSTANT.API.OFFER.CREATE_PDF.replace("{customerId}", customerId)
        const result: ApiResponse = await httpService.post(url, bodyParams);

        return result.status === CONSTANT.HTTP_STATUS.OK || result.status === CONSTANT.HTTP_STATUS.NO_CONTENT;
      } catch (error) {
        return false;
      }
    },

    async downloadPdf(customerId: string, offerId: string, fileName?: string, withPlayerConfig: boolean = true) {
      try {
        const url = CONSTANT.API.OFFER.DOWNLOAD_PDF.replace("{customerId}", customerId).replace("{offerId}", offerId)
        const params = withPlayerConfig ? {} : { withoutPlayerConfig: true }
        const result: any = await httpService.get(url, params, "blob")
        if (result?.status === CONSTANT.HTTP_STATUS.OK && result.data) {
          const blob = new Blob([result.data], { type: "application/pdf" })
          const link = document.createElement("a")
          link.href = URL.createObjectURL(blob)
          link.download = fileName || `offer-${offerId}.pdf`
          link.click()
          URL.revokeObjectURL(link.href)
          return true
        }
        return false
      } catch (error) {
        return false
      }
    },

    getUnitPriceValue(assortmentProduct: EecomTpAssortmentProduct, totalQuantites: number) {
      const customerStore = useCustomerStore()
      const calculatedPrice = getCalculatedPriceObject(assortmentProduct?.product, totalQuantites)
      const assortmentProductPrice = _getValue(assortmentProduct, "calculatedPrice.unitPrice", null).value
      
      const customer = customerStore.customerData
      let unitPrice: number = 0
      if (assortmentProductPrice !== null && assortmentProductPrice !== undefined) {
        unitPrice = assortmentProductPrice
      } else {
        const customerRegularDiscount = get(customer, "customFields.eecom_tp_customer_regular_discount", 0)
        const calculatedCheapestListPrice = _getValue(assortmentProduct?.product, "calculatedCheapestPrice.listPrice.price", 0).value
        if (calculatedCheapestListPrice) {
          const calculatedCheapestListDiscount = Number(((calculatedCheapestListPrice * customerRegularDiscount) / 100).toFixed(CONSTANT.COMMON.FRACTION_DIGITS))
          unitPrice = calculatedCheapestListPrice - calculatedCheapestListDiscount
        } else if (calculatedPrice) {
          unitPrice = _getValue(calculatedPrice, "unitPrice", 0).value
        }
      }
      return Number(unitPrice.toFixed(CONSTANT.COMMON.FRACTION_DIGITS))
    },

    getListPrice(product: Product, totalQuantites: number) {
      const calculatedCheapestListPrice = _getValue(product, "calculatedCheapestPrice.listPrice", null).value
      const calculatedPrice = getCalculatedPriceObject(product, totalQuantites)
      let price: number = 0
      if (calculatedCheapestListPrice) {
        price = _getValue(calculatedCheapestListPrice, "price", 0).value
      } else if (calculatedPrice && calculatedPrice.listPrice) {
        price = _getValue(calculatedPrice.listPrice, "price", 0).value
      }
      return price
    },

    getProductAmount(assortmentProduct: EecomTpAssortmentProduct, totalQty: number) {
      const unitPrice = this.getUnitPriceValue(assortmentProduct, totalQty)
      return getMultiplicationOfTwoNumbersWithFractionDigits(unitPrice, totalQty, CONSTANT.COMMON.FRACTION_DIGITS)
    },

    isValidChildren(assortmentProduct: EecomTpAssortmentProduct, productQuantityValues: any) {
      const children = _getValue(assortmentProduct, "product.children", []).value
      if (!productQuantityValues || children?.length === 0) {
        return true
      }
      if(this.isAdidas){
        return children.every((child: any) => {
          const availableStock = child?.availableStock
          const childQuantity = productQuantityValues[child?.id]
          return !(child.isCloseout && availableStock > 0 && childQuantity > availableStock)
        })
      }

      return true
    },

    calculateFinishesPriceInNumber(currentAssortmentItemObject: Object | undefined, assortmentProduct: EecomTpAssortmentProduct, totalQty: number) {
      const eecomTpAssortmentConfiguration = JSON.parse(_getValue(currentAssortmentItemObject, "configuration", "{}").value)
      const lineItems = _getValue(eecomTpAssortmentConfiguration, "items[0].lineItem", {}).value
      let total = 0
      let nonUserInputTotal = 0
      let userInputTotal = 0

      if (totalQty > 0) {
        const mandatoryCustomizations = assortmentProduct?.eecomTpMandatoryCustomizations ?? []
        const optionalCustomizations = assortmentProduct?.eecomTpOptionalCustomizations ?? []
        const allCustomisations = [...mandatoryCustomizations, ...optionalCustomizations]

        Object.keys(lineItems).forEach((key) => {
          const lineItem = lineItems[key]
          if (!lineItem.quantity || lineItem.quantity <= 0) return

          allCustomisations.forEach((customisation) => {
            const optionKey = lineItem.options ? Object.keys(lineItem.options).find((optionKey) => optionKey === customisation.id) : null
            const option = optionKey ? lineItem.options[optionKey] : null
            if (option) {
              const count = customisation.eecomTpCustomization.type !== CONSTANT.PLAYER_CONFIGURATIONS.TYPE.USER_INPUT ? lineItem.quantity : (option.values?.filter((v: any) => v.value !== "" && v.value !== null && v.value !== undefined).length || 0)
              const price = getOptionPrice(customisation, count)
              if (customisation.eecomTpCustomization.type !== CONSTANT.PLAYER_CONFIGURATIONS.TYPE.USER_INPUT) {
                nonUserInputTotal = getSumOfTwoNumbersWithFractionDigits(nonUserInputTotal, getTotalValue(count, price), CONSTANT.COMMON.FRACTION_DIGITS)
              } else {
                userInputTotal = getSumOfTwoNumbersWithFractionDigits(userInputTotal, getTotalValue(count, price), CONSTANT.COMMON.FRACTION_DIGITS)
              }
            }
          })
        })
        total = getSumOfTwoNumbersWithFractionDigits(nonUserInputTotal, userInputTotal, CONSTANT.COMMON.FRACTION_DIGITS)
      }
      return total
    },

    calculateProductAmounts(assortmentProduct: any, currentAssortmentItemObject: any, totalQty: number) {
      const productAmount = this.getProductAmount(assortmentProduct, totalQty)
      const calculateFinishesPriceInNumber = this.calculateFinishesPriceInNumber(currentAssortmentItemObject, assortmentProduct, totalQty)
      const subTotal = getSumOfTwoNumbersWithFractionDigits(productAmount, calculateFinishesPriceInNumber, CONSTANT.COMMON.FRACTION_DIGITS)
      return {
        productAmount: productAmount,
        calculateFinishesPrice: calculateFinishesPriceInNumber,
        subTotal: subTotal,
      }
    },

    /**
     * Reusable totals calculation for offer product list (sales-force and customer views).
     * @param products - Array of eecomTpOfferAssortmentProducts
     * @param getConfigForProduct - Async function to get config object for a product id (e.g. from IndexedDB)
     * @returns { productTotal, printTotal } raw numbers for formatting in the view
     */
    async calculateOfferTotals(
      products: any[],
      getConfigForProduct: (productId: string) => Promise<any>
    ): Promise<{ productTotal: number; printTotal: number }> {
      if (!products?.length) {
        return { productTotal: 0, printTotal: 0 }
      }
      let productTotal = 0
      let printTotal = 0
      for (const product of products) {
        // Use keyed approach (same as CustomerOfferProductItem) to avoid counting duplicate productIds
        const qtyByProductId: Record<string, number> = {}
        ;(product?.eecomTpOfferAssortmentProductConfigs || []).forEach((config: any) => {
          qtyByProductId[config.productId] = Number(config.quantity) || 0
        })
        const totalQty = Object.values(qtyByProductId).reduce((sum: number, qty: number) => sum + qty, 0)
        if (totalQty > 0) {
          const currentAssortmentItemObject = await getConfigForProduct(product.id)
          const result = this.calculateProductAmounts(product, currentAssortmentItemObject, totalQty)
          productTotal += result?.productAmount || 0
          printTotal += result?.calculateFinishesPrice || 0
        }
      }
      return { productTotal, printTotal }
    },

    getCustomConfigurationsForOffer(offerProduct: any, productQuantityValues: any, optionalOptionQuantityValues: any, configuredLineItems: any) {
      const mandatoryUserInputCustomization = getUserInputCustomisation(get(offerProduct, "eecomTpMandatoryCustomizations", []))
      const mandatoryPreFilledInputCustomization = getPreFilledInputCustomisation(get(offerProduct, "eecomTpMandatoryCustomizations", []))
      let optionalUserInputCustomization = getUserInputCustomisation(get(offerProduct, "eecomTpOptionalCustomizations", []))

      const customConfigurationArray: any = []
      const product = _getValue(offerProduct, "product", null).value

      if (product) {
        const children = sortChildren(product)

        if (children && children.length > 0) {
          children.forEach((child: any) => {
            if (productQuantityValues && productQuantityValues[child.id] >= 0) {
              if (optionalOptionQuantityValues && Object.keys(optionalOptionQuantityValues).length > 0) {
                const updatedOptions = optionalUserInputCustomization.map((option: any) => {
                  const key = `${option.id}_${child.id}`
                  if (optionalOptionQuantityValues.hasOwnProperty(key)) {
                    return { ...option, optionQty: optionalOptionQuantityValues[key] }
                  }
                  return { ...option, optionQty: 0 }
                })

                optionalUserInputCustomization = updatedOptions.slice()
              }

              const childProduct = this.createChildProductForOffer(child, productQuantityValues, mandatoryUserInputCustomization, optionalUserInputCustomization, configuredLineItems) //configuredLineItems
              customConfigurationArray.push(childProduct)
            }
          })
        } else {
          // If product is base product
          if (productQuantityValues && productQuantityValues[product.id] >= 0) {
            customConfigurationArray.push(this.createChildProductForOffer(product, productQuantityValues, mandatoryUserInputCustomization, optionalUserInputCustomization, configuredLineItems))
          }
        }

        if (customConfigurationArray && customConfigurationArray.length > 0) {
          customConfigurationArray.forEach((child: any) => {
            mandatoryPreFilledInputCustomization.forEach((c: any) => {
              child.playerConfiguration.options[c.id] = {
                type: CONSTANT.PLAYER_CONFIGURATIONS.TYPE.PREFILLED_INPUT,
              }
            })
          })
        }
        return customConfigurationArray
      }
    },

    updateAssortmentProductConfigs(config: any, assortmentConfigs: any) {
      const lineItems = config.lineItem;
      for (const childId in lineItems) {
        if (lineItems.hasOwnProperty(childId)) {
          const lineItem = lineItems[childId];
          const assortmentConfig = assortmentConfigs.find((ac: any) => ac.productId === childId);
          if (assortmentConfig) {
            for (const customizationId in lineItem.options) {
              if (lineItem.options.hasOwnProperty(customizationId)) {
                const option = lineItem.options[customizationId];
                const optionalConfig = assortmentConfig.eecomTpOfferOptionalConfig.find((opt: any) => opt.eecomTpAssortmentOptionalProductCustomizationId === customizationId);
                if (optionalConfig) {
                  if (optionalConfig.quantity > 0) {
                    optionalConfig.value = option.values;
                  } else {
                    optionalConfig.value = [];
                  }
                }
                const mandatoryConfig = assortmentConfig.eecomTpOfferMandatoryConfig.find((opt: any) => opt.eecomTpAssortmentMandatoryProductCustomizationId === customizationId);
                if (mandatoryConfig) {
                  if (mandatoryConfig.quantity > 0) {
                    mandatoryConfig.value = option.values;
                  } else {
                    mandatoryConfig.value = [];
                  }
                }
              }
            }
          }
        }
      }
    },

    createChildProductForOffer(child: Product, productQty: ProductQuantity, mandatoryUserInputCustomization: any, optionalUserInputCustomization: any, configuredLineItems?: any) {
      const childProductsObj = {
        id: child.id,
        type: "product",
        referencedId: child.id,
        stackable: true,
        removable: true,
        quantity: Number(productQty[child.id]),
        options: {},
      }
      const mandatoryOptions = this.createUserInputOptionValues(mandatoryUserInputCustomization, productQty, child.id, configuredLineItems)
      const optionalOptions = this.createUserInputOptionValues(optionalUserInputCustomization, productQty, child.id, configuredLineItems)
      childProductsObj.options = {
        ...mandatoryOptions,
        ...optionalOptions,
      }
      return {
        id: child.id,
        options: child.options,
        playerConfiguration: childProductsObj,
        mandatoryCustomization: mandatoryUserInputCustomization,
        optionalCustomization: optionalUserInputCustomization,
      }
    },

    createUserInputOptionValues(customizations: any, productQty: any, childId: any, configuredLineItems: any) {
      const options: any = {}
      customizations.forEach((customization: any) => {
        options[customization.id] = {
          type: CONSTANT.PLAYER_CONFIGURATIONS.TYPE.USER_INPUT,
          values: [],
        }

        const maxIterations = customization.hasOwnProperty("optionQty") ? customization.optionQty : productQty[childId]

        for (let i = 0; i < maxIterations; i++) {
          const amount = i + 1
          let value: any = ""

          const optionValues = configuredLineItems[childId]?.options?.[customization.id]?.values || []
          const configuredOption = optionValues[i] ? optionValues[i].value : null

          if (configuredOption && optionValues?.[i]) {
            const isValueExist = optionValues[i]?.value
            const isAmountExist = optionValues[i]?.amount
            value = isAmountExist === amount && isValueExist ? isValueExist : ""
          } else {
            value = ""
          }

          options[customization.id].values.push({
            amount,
            value,
          })
        }
      })

      return options
    },

    restCustomizationTypeObject() {
      this.customizationTypeObject = {
        initials: {
          mandatoryCustomization: {},
          optionalCustomization: {},
        },
        playername: {
          mandatoryCustomization: {},
          optionalCustomization: {},
        },
        number: {
          mandatoryCustomization: {},
          optionalCustomization: {},
        },
        expertMode: {
          mandatoryCustomization: {},
          optionalCustomization: {},
        },
      }
    },

    setPlayerConfigurations(offerProduct: any, productQuantityValues: any, optionalOptionQuantityValues: any, configuredLineItems: any, t: any) {
      this.finalConfiguration = {} as FinalConfiguration
      const customizations = {
        mandatory: getUserInputCustomisation(get(offerProduct, "eecomTpMandatoryCustomizations", [])),
        optional: getUserInputCustomisation(get(offerProduct, "eecomTpOptionalCustomizations", [])),
        preFilled: getPreFilledInputCustomisation(get(offerProduct, "eecomTpMandatoryCustomizations", [])),
      }

      const customConfigurationArray = this.getCustomConfigurationsForOffer(offerProduct, productQuantityValues, optionalOptionQuantityValues, configuredLineItems)
      // Set Headers
      const createHeaders = (product: any, customizations: any) => {
        let headers: FinalConfigurationHeader[] = []
        const isProductChild = !!(customConfigurationArray && customConfigurationArray.length === 1 && product.id === customConfigurationArray[0]?.id)

        if (!isProductChild) {
          headers = [
            {
              key: "product",
              label: t("eecom-teamportal.playerConfiguration.size"),
            },
          ]
        }

        Object.entries(customizations).forEach(([type, customizationList]: any) => {
          if (type !== "preFilled") {
            customizationList.forEach((c: any) => {
              // Check if the customization optionQty is greater than 0 in any of the customConfigurationArray objects
              let hasValidOptionQty = false;
              if (type === "optional") {
                hasValidOptionQty = cloneDeep(customConfigurationArray).some((config: any) =>
                  config.optionalCustomization?.some(
                    (option: any) => option.id === c.id && option.optionQty > 0
                  )
                );
              }
              if (hasValidOptionQty) {
                  const {customizationLabel} = getCustomizationNameFromOption(
                    c.eecomTpCustomization,
                    false,
                    t
                  );
                  headers.push({key: c.id, label: customizationLabel});

              } else {
                const {customizationLabel} = getCustomizationNameFromOption(
                  c.eecomTpCustomization,
                  type === "mandatory",
                  t
                );
                headers.push({key: c.id, label: customizationLabel});
              }
            })
          }
        })
        return headers
      }

      const headers = createHeaders(offerProduct.product, customizations)

      this.finalConfiguration = {
        id: offerProduct.eecomTpAssortmentProductId,
        headers: headers,
        children: customConfigurationArray,
      }

      this.restCustomizationTypeObject()
      this.handleCustomizationByType("mandatoryCustomization", configuredLineItems)
      this.handleCustomizationByType("optionalCustomization", configuredLineItems)
    },

    updatePlayerConfigurations(newValue: any, customizationId: any, item: any, qIndex: number) {
      const optionValue = item.playerConfiguration.options[customizationId].values[qIndex]
      if (optionValue) {
        optionValue.value = newValue
      } else {
        item.playerConfiguration.options[customizationId].values.push({amount: qIndex + 1, value: newValue})
      }

      const updatedChildren = this.finalConfiguration.children.map((child: any) => {
        if (child.id === item.id) {
          return {...child, playerConfiguration: item.playerConfiguration}
        }
        return child
      })

      this.finalConfiguration = {...this.finalConfiguration, children: updatedChildren}
    },

    parseFinishesFromConfiguration(product: any) {
      const finishes: any = []

      if (!this.finalConfiguration) {
        return []
      }

      const processCustomizations = (customizations: any, customizationType: string, prefix: string) => {
        customizations.forEach(async (customization: any, index: number) => {
          const id = `${prefix}_${index}`
          const label = getCustomisationName(customization)
          let count = 0
          let price = 0
          let total = 0

          const c = customization.eecomTpCustomization
          if (!c.type || c.type !== CONSTANT.PLAYER_CONFIGURATIONS.TYPE.USER_INPUT) {
            count = getTotalPlayerCount(this.finalConfiguration)
          } else {
            const typeKey = getTypeKey(c.validationRule, customization)
            const keys = Object.keys(this.customizationTypeObject[typeKey][customizationType])
            const uniqueKeys = keys.filter(key => key.includes(`_c_${customization.id}_`))
            count = uniqueKeys.length
          }

          price = getOptionPrice(customization, count)
          total = getTotalValue(count, price)

          finishes.push({id, label, count, price, total})
        })
      }

      processCustomizations(product?.eecomTpMandatoryCustomizations || [], "mandatoryCustomization", "finish_m")
      processCustomizations(product?.eecomTpOptionalCustomizations || [], "optionalCustomization", "finish_o")
      const result = sortBy(finishes, ["label"])
      return result
    },

    getAssortmentProductMedia(assortmentProduct: EecomTpAssortmentProduct) {
      const data = sortBy(assortmentProduct?.eecomTpAssortmentProductMedia, function (o) {
        return !o.isCover
      })

      const images = data?.map((item: any) => ({
        id: item.id,
        path: getAssortmentProductMediaUrl(item),
      }))
      return images
    },

    getAssortmentProductMediaCover(assortmentProduct: EecomTpAssortmentProduct) {
      const images = [
        {
          id: assortmentProduct?.id,
          path: getAssortmentProductMediaCoverUrl(assortmentProduct),
        },
      ]
      return images
    },

    calculateTotalFinishesPriceObject(finishes: any, t: any) {
      const finalObject: any = {
        label: "",
        total: 0,
        formatedTotal: 0,
      }

      let count = 0
      let totalPrice = 0
      finishes.forEach((finish: any) => {
        count += finish.count
        totalPrice = getSumOfTwoNumbersWithFractionDigits(finish.total, totalPrice, CONSTANT.COMMON.FRACTION_DIGITS)
      })
      finalObject.label = `${count} x ${t("eecom-teamportal.teamOrder.finishes")}`
      finalObject.total = totalPrice
      finalObject.formatedTotal = formatPrice(totalPrice)
      return finalObject
    },

    calculateSubTotal(assortmentProduct: EecomTpAssortmentProduct, totalFinishesPrice: any, totalQty: number) {
      return getSumOfTwoNumbersWithFractionDigits(this.getProductAmount(assortmentProduct, totalQty), totalFinishesPrice, CONSTANT.COMMON.FRACTION_DIGITS)
    },

    getUnitPrice(assortmentProduct: EecomTpAssortmentProduct, totalQty: number) {
      return this.getUnitPriceValue(assortmentProduct, totalQty)
    },

    updateCustomizationValueObjectByType(child: any, customizationType: string, customization: any, value: any, index: number, amount: number) {
      const validationRule = customization?.eecomTpCustomization?.validationRule
      const typeKey = getTypeKey(validationRule, customization)
      const key = `ch_${child.id}_c_${customization.id}_i_${index}_a_${amount}`
      if (value) {
        this.customizationTypeObject[typeKey][customizationType][key] = value
      } else {
        delete this.customizationTypeObject[typeKey][customizationType][key]
      }
    },

    handleCustomizationByType(customizationType: any, lineItems: any) {
      if (!this.finalConfiguration) {
        return []
      }
      this.finalConfiguration?.children?.forEach((child: any) => {
        if (lineItems[child.id]) {
          for (let i = 0; i < lineItems[child.id]?.quantity; i++) {
            if (child[customizationType] && child[customizationType].length > 0) {
              child[customizationType].forEach((customization: any) => {
                if (lineItems[child.id] && lineItems[child.id].options[customization.id] && lineItems[child.id].options[customization.id].values) {
                  lineItems[child.id].options[customization.id].values.forEach((item: any, index: number) => {
                    const _amount = i > 0 ? i + 1 : 1
                    if (item.amount === _amount) {
                      this.updateCustomizationValueObjectByType(child, customizationType, customization, item.value, index, _amount)
                    }
                  })
                }
              })
            }
          }
        }
      })
    },

    async addOrUpdateLineItem(
      assortmentProduct: EecomTpAssortmentProduct, 
      productQuantityValues: any,
      optionalOptionQuantityValues: any,
      isUserConsent: boolean,
      isConfigurable: boolean,
      dbService: IndexedDbService
    ) {
      const id = _getValue(assortmentProduct, "id", CONSTANT.COMMON.FIELD_NULL).value
      await dbService.connectWithIndexedDb()
      const assortmentItemObject = await dbService.get(offerConfigDBStoreName, id)

      const updateAt = new Date().getTime()

      const configuration = JSON.parse(_getValue(assortmentItemObject, "configuration", "{}").value)
      const configuredLineItems = _getValue(configuration, "items[0].lineItem", {}).value
      const customConfigurationArray = this.getCustomConfigurationsForOffer(assortmentProduct, productQuantityValues, optionalOptionQuantityValues, configuredLineItems)

      const finalConfigurationArray = {children: customConfigurationArray}
      finalConfigurationArray.children.forEach((c: any) => {
        if (c.id) {
          configuredLineItems[c.id] = c.playerConfiguration
        }
      })

      const tempCartObj = prepareCartObject(assortmentProduct, isUserConsent, isConfigurable, configuredLineItems, finalConfigurationArray, false)
      const tempLineItems: Record<string, LineItem> = get(tempCartObj, "items[0].lineItem", {})

      if (tempLineItems && Object.keys(tempLineItems).length > 0) {
        Object.keys(tempLineItems).forEach((key: string) => {
          if (tempLineItems[key] && tempLineItems[key].quantity === 0) {
            delete tempLineItems[key]
          }
        })
      }

      if (Object.keys(tempLineItems).length > 0) {
        const obj = {id: id, configuration: JSON.stringify(tempCartObj), updatedAt: updateAt}
        await dbService.set(offerConfigDBStoreName, obj)
      } else {
        await dbService.delete(offerConfigDBStoreName, id)
      }

      IndexDBChanged.setIndexDBChangedSuccess(true)
    },

    async updateConfigurationInIndexDB(
      assortmentProduct: EecomTpAssortmentProduct,
      lineItems: any,
      dbService: any,
      dbStoreName: string,
      isUserConsent: boolean
    ) {
      const id = _getValue(assortmentProduct, "id", CONSTANT.COMMON.FIELD_NULL).value
      await dbService.connectWithIndexedDb()
      const updateAt = new Date().getTime()

      const tempCartObj = prepareCartObject(assortmentProduct, isUserConsent, true, lineItems, this.finalConfiguration, false)
      const obj = {id: id, configuration: JSON.stringify(tempCartObj), updatedAt: updateAt}
      await dbService.set(dbStoreName, obj)
      this.hasUnsavedChanges = true
      IndexDBChanged.setIndexDBChangedSuccess(true)

      this.restCustomizationTypeObject()
      this.handleCustomizationByType("mandatoryCustomization", lineItems)
      this.handleCustomizationByType("optionalCustomization", lineItems)
    },
    
    setLineItems(finalConfiguration: FinalConfiguration) {
      const _lineItems: any = {}
      if (!finalConfiguration) {
        return
      }

      finalConfiguration.children?.forEach((child: any) => {
        if (child && child.id) {
          _lineItems[child?.id] = child?.playerConfiguration
        }
      })

      return _lineItems
    },
    
    updateOfferProductConfig(offerProductId: string, productId: string, qty: any) {
      const quantity = Number(qty) || 0

      const products = this.offer?.eecomTpAssortment?.eecomTpAssortmentProducts
      if (!Array.isArray(products) || products.length === 0) {
        return
      }

      const offerProductIndex = products.findIndex((p: any) => p.id === offerProductId)
      if (offerProductIndex === -1) {
        return
      }
      this.hasUnsavedChanges = true
      this.dirtyProductIds.add(offerProductId)

      const offerProduct = products[offerProductIndex]

      if (!Array.isArray(offerProduct.eecomTpOfferAssortmentProductConfigs)) {
        offerProduct.eecomTpOfferAssortmentProductConfigs = []
      }

      const configs = offerProduct.eecomTpOfferAssortmentProductConfigs
      const existingIndex = configs.findIndex((config: any) => config.productId === productId)

      if (existingIndex === -1) {
        const newConfig = {
          id: uuid.randomHex(),
          productId,
          quantity,
          eecomTpOfferOptionalConfig: [],
          eecomTpOfferMandatoryConfig: [],
        }
        configs.push(newConfig)
      } else {
        const existing = configs[existingIndex]
        configs[existingIndex] = {
          id: existing.id,
          productId: existing.productId,
          quantity,
          eecomTpOfferOptionalConfig: quantity ? existing.eecomTpOfferOptionalConfig : [],
          eecomTpOfferMandatoryConfig: quantity ? existing.eecomTpOfferMandatoryConfig : [],
        }
      }

      this.offer.eecomTpAssortment.eecomTpAssortmentProducts[offerProductIndex] = {
        ...offerProduct,
        eecomTpOfferAssortmentProductConfigs: configs,
      }

      const mandatoryOptions = offerProduct?.eecomTpMandatoryCustomizations || []
      mandatoryOptions.forEach((option: any) => {
        if (option && option.eecomTpCustomization && option.eecomTpCustomization.type !== CONSTANT.PLAYER_CONFIGURATIONS.TYPE.PREFILLED_INPUT) {
          this.updateOfferMandatoryConfigForOption(offerProductId, productId, option.id, quantity)
        }
      })
    },

    updateOfferOptionalConfigForOption(offerProductId: string, productId: string, optionId: string, qty: any = 0) {
      const quantity = Number(qty) || 0

      const products = this.offer?.eecomTpAssortment?.eecomTpAssortmentProducts
      if (!Array.isArray(products) || products.length === 0) {
        return
      }

      const offerProductIndex = products.findIndex((p: any) => p.id === offerProductId)
      if (offerProductIndex === -1) {
        return
      }

      const offerProduct = products[offerProductIndex]

      const productConfigIndex = offerProduct.eecomTpOfferAssortmentProductConfigs.findIndex((config: any) => config.productId === productId)
     
      if(productConfigIndex !== -1) {
        const productConfig = offerProduct.eecomTpOfferAssortmentProductConfigs[productConfigIndex]
        if (!Array.isArray(productConfig.eecomTpOfferOptionalConfig)) {
          productConfig.eecomTpOfferOptionalConfig = []
        }
        const existingIndex = productConfig.eecomTpOfferOptionalConfig.findIndex(
          (config: any) => config.eecomTpAssortmentOptionalProductCustomizationId === optionId
        )

        if (existingIndex === -1) {
          productConfig.eecomTpOfferOptionalConfig.push({
            eecomTpAssortmentOptionalProductCustomizationId: optionId,
            quantity,
            value: [],
          })
        } else {
          const existing = productConfig.eecomTpOfferOptionalConfig[existingIndex]
          productConfig.eecomTpOfferOptionalConfig[existingIndex] = {
            id: existing.id,
            eecomTpAssortmentOptionalProductCustomizationId: existing.eecomTpAssortmentOptionalProductCustomizationId,
            quantity,
            value: quantity > 0 ? existing.value : [],
          }
        }
      }  


      // Ensure reactivity by assigning the updated product back
      this.offer.eecomTpAssortment.eecomTpAssortmentProducts[offerProductIndex] = {
        ...offerProduct,
        eecomTpOfferAssortmentProductConfigs: [...offerProduct.eecomTpOfferAssortmentProductConfigs],
      }
    },

    updateOfferMandatoryConfigForOption(offerProductId: string, productId: string, optionId: string, qty: any = 0) {
      const quantity = Number(qty) || 0

      const products = this.offer?.eecomTpAssortment?.eecomTpAssortmentProducts
      if (!Array.isArray(products) || products.length === 0) {
        return
      }

      const offerProductIndex = products.findIndex((p: any) => p.id === offerProductId)
      if (offerProductIndex === -1) {
        return
      }

      const offerProduct = products[offerProductIndex]
      if (!Array.isArray(offerProduct.eecomTpOfferAssortmentProductConfigs)) {
        offerProduct.eecomTpOfferAssortmentProductConfigs = []
      }

      const productConfigIndex = offerProduct.eecomTpOfferAssortmentProductConfigs.findIndex((config: any) => config.productId === productId)
      if (productConfigIndex === -1) {
        return
      }

      const productConfig = offerProduct.eecomTpOfferAssortmentProductConfigs[productConfigIndex]
      if (!Array.isArray(productConfig.eecomTpOfferMandatoryConfig)) {
        productConfig.eecomTpOfferMandatoryConfig = []
      }

      const existingIndex = productConfig.eecomTpOfferMandatoryConfig.findIndex(
        (config: any) => config.eecomTpAssortmentMandatoryProductCustomizationId === optionId
      )

      if (existingIndex === -1) {
        productConfig.eecomTpOfferMandatoryConfig.push({
          id: uuid.randomHex(),
          eecomTpAssortmentMandatoryProductCustomizationId: optionId,
          quantity,
          value: [],
        })
      } else {
        const existing = productConfig.eecomTpOfferMandatoryConfig[existingIndex]
        productConfig.eecomTpOfferMandatoryConfig[existingIndex] = {
          id: existing.id,
          eecomTpAssortmentMandatoryProductCustomizationId: existing.eecomTpAssortmentMandatoryProductCustomizationId,
          quantity,
          value: quantity > 0 ? existing.value : [],
        }
      }

      // Ensure reactivity by assigning the updated product back
      this.offer.eecomTpAssortment.eecomTpAssortmentProducts[offerProductIndex] = {
        ...offerProduct,
        eecomTpOfferAssortmentProductConfigs: [...offerProduct.eecomTpOfferAssortmentProductConfigs],
      }
    },

    syncOfferProductConfigsFromLineItems(assortmentProductId: string, lineItems: any) {
      if (!lineItems || typeof lineItems !== 'object') {
        return
      }

      const products = this.offer?.eecomTpAssortment?.eecomTpAssortmentProducts
      if (!Array.isArray(products) || products.length === 0) {
        return
      }

      // Find the offer product by assortmentProductId
      const offerProduct = products.find((p: any) => p.eecomTpAssortmentProductId === assortmentProductId)
      if (!offerProduct || !Array.isArray(offerProduct.eecomTpOfferAssortmentProductConfigs)) {
        return
      }

      // Iterate through lineItems (each childId/productId)
      for (const childId in lineItems) {
        if (lineItems.hasOwnProperty(childId)) {
          const lineItem = lineItems[childId]
          const productConfig = offerProduct.eecomTpOfferAssortmentProductConfigs.find(
            (config: any) => config.productId === childId
          )

          if (!productConfig) {
            continue
          }

          // Update values for each customization option in the lineItem
          if (lineItem.options && typeof lineItem.options === 'object') {
            for (const customizationId in lineItem.options) {
              if (lineItem.options.hasOwnProperty(customizationId)) {
                const option = lineItem.options[customizationId]
                const optionalConfig = productConfig.eecomTpOfferOptionalConfig?.find(
                  (opt: any) => opt.eecomTpAssortmentOptionalProductCustomizationId === customizationId
                )
                if (optionalConfig) {
                  if (optionalConfig.quantity > 0 && option.values) {
                    optionalConfig.value = option.values
                  } else {
                    optionalConfig.value = []
                  }
                }

                const mandatoryConfig = productConfig.eecomTpOfferMandatoryConfig?.find(
                  (opt: any) => opt.eecomTpAssortmentMandatoryProductCustomizationId === customizationId
                )
                if (mandatoryConfig) {
                  if (mandatoryConfig.quantity > 0 && option.values) {
                    mandatoryConfig.value = option.values
                  } else {
                    mandatoryConfig.value = []
                  }
                }
              }
            }
          }
        }
      }

      // Ensure reactivity by updating the offer product
      const offerProductIndex = products.findIndex((p: any) => p.eecomTpAssortmentProductId === assortmentProductId)
      if (offerProductIndex !== -1) {
        this.offer.eecomTpAssortment.eecomTpAssortmentProducts[offerProductIndex] = {
          ...offerProduct,
          eecomTpOfferAssortmentProductConfigs: [...offerProduct.eecomTpOfferAssortmentProductConfigs],
        }
      }
    },

    updateOfferOptionalConfigQuantity(offerProductId: string, productId: string, optionId: string, qty: any) {
      const quantity = Number(qty) || 0
      this.hasUnsavedChanges = true
      this.dirtyProductIds.add(offerProductId)
      this.updateOfferOptionalConfigForOption(offerProductId, productId, optionId, quantity)
    },

    checkValidConfig(assortmentProduct: EecomTpAssortmentProduct, configuration: any, productQuantityValues: any, optionalOptionQuantityValues: any, isUserConsent: boolean) {
      let isValid = true
      const product = _getValue(assortmentProduct, "product", null).value
      if (!configuration || !configuration?.lineItem) {
        return false
      }
      const configuredLineItems = configuration?.lineItem
      const customConfigurationArray = getCustomConfigurationArrayV3(optionalOptionQuantityValues, productQuantityValues, assortmentProduct, product, configuredLineItems)
      customConfigurationArray.forEach((child: any) => {
        if (child.playerConfiguration) {
          for (let i = 0; i < child.playerConfiguration.quantity; i++) {
            if (child.mandatoryCustomization && child.mandatoryCustomization.length > 0) {
              for (let k = 0; k < child.mandatoryCustomization.length; k++) {
                const mc = child.mandatoryCustomization[k]
                const item = configuredLineItems[child.id]?.options?.[mc.id]?.values?.find((v: any) => v.amount === i + 1)
                if (mc.eecomTpCustomization.expertMode && isValid) {
                  const expertModeRegex = initRegExForExpertMode(mc.eecomTpCustomization.validationRule, "")
                  const itemValue = item ? item.value : null
                  if (isValidationErrorInExpertMode(itemValue, expertModeRegex) || (!isUserConsent && !itemValue)) {
                    isValid = false
                    break
                  }
                } else {
                  if (!item || (item && getValueLength(item.value) === 0)) {
                    isValid = false
                    return
                  }
                }
              }
            }
          }
        }
      })
      return isValid
    },

    updateCustomizationPriceStore(price: any, productId: string, customizationId: string, type: string) {
      this.dirtyProductIds.add(productId)
      this.hasUnsavedChanges = true
      const updatePrice = (customizations: any[]) => {
        const customization = customizations.find((mc: any) => mc.id === customizationId)
        if (customization) {
          customization.price = price
          if (customization.calculatedPrice) {
            customization.calculatedPrice.unitPrice = price[0].gross
          } else {
            customization.calculatedPrice = { unitPrice: price[0].gross }
          }
        }
      }

      const index = this.offer.eecomTpAssortment.eecomTpAssortmentProducts.findIndex((item: any) => item.id === productId)
      if (index !== -1) {
        const product = this.offer.eecomTpAssortment.eecomTpAssortmentProducts[index]

        if (type === "mandatory") {
          updatePrice(product?.eecomTpMandatoryCustomizations)
        } else if (type === "optional") {
          updatePrice(product?.eecomTpOptionalCustomizations)
        }
      }
    },

    updateAssortmentProductsStore(product: any, value?: any, key?: any, options: { markDirty?: boolean } = {}) {
      if (options.markDirty && product?.id) {
        this.dirtyProductIds.add(product.id)
      }
      // NOTE: hasUnsavedChanges is set by the user-input handlers in OfferProductItem
      // (updatePercentage / updatePrice). This action also runs from an immediate
      // price watch on mount, so flipping the flag here would mark every fresh
      // page load as dirty. markDirty is false for that sync path.
      const products = this.offer?.eecomTpAssortment?.eecomTpAssortmentProducts
      const index = Array.isArray(products) ? products.findIndex((item: any) => item.id === product?.id) : -1
      if (index !== -1) {
        const currentProduct = products[index]
        const updatedProduct: any = {
          ...currentProduct,
          [key]: value,
        }
        if (key === "price" && Array.isArray(value) && value[0]?.gross !== undefined) {
          updatedProduct.calculatedPrice = {
            ...(currentProduct.calculatedPrice || {}),
            unitPrice: value[0].gross,
          }
        }
        this.offer.eecomTpAssortment.eecomTpAssortmentProducts[index] = updatedProduct
      }
    },

    updateOfferStore(offer: any, value?: any, key?: any) {
        this.offer = {
          ...offer,
          [key]: value,
        }
    },

    async autoSaveOfferProductConfiguration(offerProduct: any, dbService: IndexedDbService, dbStoreName: string) {  
      const offerProductId = _getValue(offerProduct, "id", "").value
      if (!offerProductId) {
        return
      }
      const offerProductItemObject = await dbService.get(dbStoreName, offerProductId);
      const configuration = parseConfigurationFromItemObject(offerProductItemObject);

      if(!configuration || !configuration?.lineItem) {
        await dbService.delete(dbStoreName, offerProductId)
      } else {
        configuration.lineItem = removeEmptyOptionalCustomizationV2(this.finalConfiguration, configuration.lineItem, true)
      }

      const existingConfiguration = offerProduct.configuration;
      if (!isEmpty(configuration) && !isEqual(existingConfiguration?.items[0], configuration)) {
        await this.setConfiguration({
          id: offerProduct.id,
          configuration: JSON.parse(_getValue(offerProductItemObject, "configuration", "{}").value)
        }, false)

        return true
      }
      return false
    },

    async saveProductConfig(bodyParams: any, isLoader: boolean = true) {
      try {
        const url = CONSTANT.API.OFFER.SET_OFFER_PRODUCT_CONFIG
        const http = isLoader ? httpService : httpServiceWithoutLoader
        return await http.post(url, bodyParams)
      } catch (error) {
        return get(error, `response.data.errors`, [])
      }
    },

    async setConfiguration(bodyParams: any, isLoader: boolean = true,) {
      try {
        const setConfigurationUrl = CONSTANT.API.OFFER.SET_OFFER_ASSORTMENT_PRODUCT_CONFIG;
        let result: any
        if (isLoader) {
          result = await httpService.post(setConfigurationUrl, bodyParams)
        } else {
          result = await httpServiceWithoutLoader.post(setConfigurationUrl, bodyParams)
        }
        return result
      } catch (error) {
        return get(error, `response.data.errors`, [])
      }
    },

    getProductQuantitiesForProduct(product: any): Record<string, number> {
      const quantities: Record<string, number> = {}
      const configs = product?.eecomTpOfferAssortmentProductConfigs || []
      configs.forEach((config: any) => {
        quantities[config.productId] = Number(config?.quantity ?? 0)
      })
      return quantities
    },

    getOptionalQuantitiesForProduct(product: any): Record<string, number> {
      const quantities: Record<string, number> = {}
      const configs = product?.eecomTpOfferAssortmentProductConfigs || []
      configs.forEach((config: any) => {
        ;(config.eecomTpOfferOptionalConfig || []).forEach((optionalConfig: any) => {
          const key = `${optionalConfig.eecomTpAssortmentOptionalProductCustomizationId}_${config.productId}`
          quantities[key] = Number(optionalConfig?.quantity ?? 0)
        })
      })
      return quantities
    },
    
    getProductValidity(product: any) {
      const productQuantities = this.getProductQuantitiesForProduct(product)
      const totalQty = Object.values(productQuantities).reduce((sum, qty) => sum + (typeof qty === "number" ? qty : 0), 0)
      const isConfigurable = checkConfigurableV3(product)
      const isValidChildren = this.isValidChildren(product, productQuantities)
      const isOnlyPrefilledCustomization = checkOnlyPrefilledCustomizationV3(product)

      return {
        totalQty,
        isValidChildren,
        isConfigurable,
        productQuantities,
        isOnlyPrefilledCustomization
      }
    },

    async prepareCartPayload(products: any[], dbService: IndexedDbService, dbStoreName: string, offerId?: string, offerName?: string) {
      const offerLineItems: any[] = []

      for (const offerProduct of products) {
        const offerProductItemObject = await dbService.get(dbStoreName, offerProduct.id);
        const configuration = parseConfigurationFromItemObject(offerProductItemObject);
        if(!configuration || !configuration?.lineItem) {
          continue
        }
        const lineItems = configuration?.lineItem;
        const item = this.generateOfferLineItem(offerProduct, lineItems);
        offerLineItems.push(item);
      }

      return {
        items: [{
          type: CONSTANT.COMMON.LINE_ITEM.OFFER_TYPE,
          referencedId: offerId || "",
          label: offerName || "",
          offerLineItems,
        }],
      }
    },

    createOptionMaps(offerProduct: any) {
      const mandatoryCustomizations = get(offerProduct, "eecomTpMandatoryCustomizations", [])
      const optionalCustomizations = get(offerProduct, "eecomTpOptionalCustomizations", [])

      const mandatoryOptionMap: any = new Map(mandatoryCustomizations.map((cust: any) => [cust.id, cust.eecomTpCustomizationId]));
      const optionalOptionMap: any = new Map(optionalCustomizations.map((cust: any) => [cust.id, cust.eecomTpCustomizationId]));

      return {mandatoryOptionMap, optionalOptionMap};
    },

    getEecomTpCustomizationIdByOptionId(offerProduct: any, id: string): string | undefined {
      const {mandatoryOptionMap, optionalOptionMap} = this.createOptionMaps(offerProduct);

      if (mandatoryOptionMap.has(id)) {
        return mandatoryOptionMap.get(id);
      }

      if (optionalOptionMap.has(id)) {
        return optionalOptionMap.get(id);
      }

      return undefined;
    },

    createCustomizationMaps(offerProduct: any) {
      const mandatoryCustomizations = get(offerProduct, "eecomTpMandatoryCustomizations", [])
      const optionalCustomizations = get(offerProduct, "eecomTpOptionalCustomizations", [])

      const mandatoryCustomizationMap: any = new Map(mandatoryCustomizations.map((cust: any) => [cust.eecomTpCustomizationId, cust.id]));
      const optionalCustomizationMap: any = new Map(optionalCustomizations.map((cust: any) => [cust.eecomTpCustomizationId, cust.id]));

      return {mandatoryCustomizationMap, optionalCustomizationMap};
    },

    getCustomizationId(offerProduct: any, eecomTpCustomizationId: string): string | undefined {
      const {mandatoryCustomizationMap, optionalCustomizationMap} = this.createCustomizationMaps(offerProduct);

      if (mandatoryCustomizationMap.has(eecomTpCustomizationId)) {
        return mandatoryCustomizationMap.get(eecomTpCustomizationId);
      }

      if (optionalCustomizationMap.has(eecomTpCustomizationId)) {
        return optionalCustomizationMap.get(eecomTpCustomizationId);
      }

      return undefined;
    },

    generateOfferLineItem(offerProduct: any, lineItems: any) {
      const assortmentProductId = _getValue(offerProduct, "id", "").value
      const product = _getValue(offerProduct, "product", "").value
      const children = _getValue(offerProduct, "product.children", []).value

      const { productQuantities, isConfigurable, isOnlyPrefilledCustomization } = this.getProductValidity(offerProduct);
      const customConfigurationChildArray = getCustomConfigurationArrayV3({}, productQuantities, offerProduct, product, lineItems)

      const lineItemMap: any = {}

      if(children && children.length > 0) {
        Object.keys(productQuantities).forEach((key: string) => {
          if(productQuantities[key] > 0) {
            lineItemMap[key] = {
              id: key,
              referencedId: key,
              quantity: Number(productQuantities[key]) ?? CONSTANT.COMMON.DEFAULT_PRODUCT_SIZE,
            }
          }
        })
      } else if(product && product.id) {
        lineItemMap[product.id] = {
          id: product.id,
          referencedId: product.id,
          quantity: Number(productQuantities[product.id]) ?? CONSTANT.COMMON.DEFAULT_PRODUCT_SIZE,
        }
      }

      if(customConfigurationChildArray && customConfigurationChildArray.length > 0) {
        customConfigurationChildArray.forEach((child: any) => {
          lineItemMap[child.id] = child.playerConfiguration
        })
      }

      // Filter zero-quantity items
      if(lineItemMap && Object.keys(lineItemMap).length > 0) {
        Object.keys(lineItemMap).forEach((key: string) => {
          if(lineItemMap[key] && Number(lineItemMap[key].quantity) === 0) {
            delete lineItemMap[key]
          }
        })
      }

      // Convert lineItem map to array
      const lineItemArray = Object.values(lineItemMap).map((li: any) => ({
        id: li.id,
        referencedId: li.referencedId,
        quantity: li.quantity,
        ...(li.options ? { options: li.options } : {}),
      }))

      return {
        id: uuid.randomHex(),
        referencedId: assortmentProductId,
        offerProductId: offerProduct.id,
        isConfigurableProduct: isOnlyPrefilledCustomization ? true : isConfigurable,
        lineItem: lineItemArray,
      }
    },

    clearOffer() {
      this.offer = {}
      this.hasUnsavedChanges = false
      this.dirtyProductIds.clear()
    },

    /** Keep unsaved offer name in the store while the user edits the form. */
    syncPendingOfferName(name: string) {
      if (!this.offer?.id) {
        return
      }
      if ((name || "") !== (this.offer.name || "")) {
        this.offer = { ...this.offer, name }
      }
    },

    markOfferSaved() {
      this.hasUnsavedChanges = false
      this.dirtyProductIds.clear()
    },

  },
})
